/*
 * @license
 * Copyright© Celligence International, LLC - 2023
 *
 * This software program is protected by copyright laws and is the property of
 * Celligence International, LLC (Celligence). Any unauthorized reproduction,
 * distribution, modification, or public display of this program, without the
 * explicit permission of Celligence is strictly prohibited. If you have gained
 * access to this program without the expressed approval of Celligence report
 * the incident to legal@celligence.com.
 *
 */
import React, { useEffect, useState, lazy, Suspense } from "react";
import { Col, Container, Row, Table, Modal, Spinner, Button } from "react-bootstrap";
import {
  tableHeaderName,
  tableHeaderConv,
  tableHeaderFha,
  addHundred,
  tableFormat,
  PAR_BASIS,
} from "../Utils";
import "../css/pricingtable.css";
import axios from "axios";

const ApplyAndDownloadForm = lazy(() => import("./ApplyAndDownloadForm"));
// import GallerySection from "./GallerySection";
const defaultValue = [];
// let obj = {};

export const WaterMarkNormal = ({ className, truthyCondtnToRender }) => {
  return (
    <>
      {truthyCondtnToRender && (
        <div className={`newline ${className}`}>
          <span style={{ whiteSpace: "nowrap" }}>For Licensed Brokers</span>
          <br />
          <span>Only</span>
        </div>
      )}
    </>
  );
};

const isNegative = (num) => {
  if (num < 0) {
    return true;
  }

  return false;
}

// td element for the rates tables
const FormulateRate = ({dataArray, i}) => {
  //console.log("dataArray: " + JSON.stringify(dataArray));
  return (
  <>
    <td className="text-center">
      {`${parseFloat(dataArray[i].rate).toFixed(3)}%`}
      &nbsp;&nbsp;
      <span className="rates-ternary-text">at&nbsp;&nbsp;{dataArray[i].desc}</span>
    </td>
  </>
  )
};

const PricingTable = (props) => {
  const [fhaData, setFhaData] = useState([]);
  const [vaData, setVaData] = useState([]);
  const [convData, setConvData] = useState([]);
  const [usdaData, setUsdaData] = useState([]);
  const [errorMessage, setErrorMessage] = useState(false);
  const [downloadModal, setDownloadModal] = useState(false);

  const closeModal = () => setDownloadModal(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const controller = new AbortController();

    const getPriceData = async () => {
      try {
        const response = await axios.get(
          "https://swmc-rate-quote-products.s3.us-west-1.amazonaws.com/production/sunwestPartners/default.json?t=" + Date.now(),
          { signal: controller.signal }
        );

        // Process conventional data
        if (response.data?.conv) {
          const priceMatrix = response.data.conv.data?.length > 0
            ? response.data.conv.data[0].priceMatrix[15]
            : [];

          const data = loadRatesFromPricing(addHundred, priceMatrix);
          
          if (data?.length > 0) {
            setConvData(data.length > 10 ? [...data.slice(0, 10)] : [...data]);
          } else {
            setConvData([...defaultValue]);
            setErrorMessage(true);
          }
        }

        // Process FHA data
        if (response.data?.fha) {
          const priceMatrix = response.data.fha.data?.length > 0
            ? response.data.fha.data[0].priceMatrix[15]
            : [];

          const data = loadRatesFromPricing(addHundred, priceMatrix);
          
          if (data?.length > 0) {
            setFhaData(data.length > 10 ? [...data.slice(0, 10)] : [...data]);
          } else {
            setFhaData([...defaultValue]);
            setErrorMessage(true);
          }
        }

        // Process USDA data
        if (response.data?.usda) {
          const priceMatrix = response.data.usda.data?.length > 0
            ? response.data.usda.data[0].priceMatrix[15]
            : [];

          const data = loadRatesFromPricing(addHundred, priceMatrix);
          
          if (data?.length > 0) {
            setUsdaData(data.length > 10 ? [...data.slice(0, 10)] : [...data]);
          } else {
            setUsdaData([...defaultValue]);
            setErrorMessage(true);
          }
        }

        // Process VA data
        if (response.data?.va) {
          const priceMatrix = response.data.va.data?.length > 0
            ? response.data.va.data[0].priceMatrix[15]
            : [];

          const data = loadRatesFromPricing(addHundred, priceMatrix);
          
          if (data?.length > 0) {
            setVaData(data.length > 10 ? [...data.slice(0, 10)] : [...data]);
          } else {
            setVaData([...defaultValue]);
            setErrorMessage(true);
          }
        }

      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error('Error fetching price data:', error);
          setFhaData([...defaultValue]);
          setConvData([...defaultValue]);
          setUsdaData([...defaultValue]);
          setVaData([...defaultValue]);
          setErrorMessage(true);
        }
      } finally {
        //
      }
    };

    getPriceData();

    return () => {
      controller.abort();
    };
  }, []);

  const loadRatesFromPricing = (addHundred, ratesData) => {
    let bpsPointsData = [];
    //add 0.5 basis point to the default price array
    addHundred = addHundred.map((value) => value + PAR_BASIS);
    //console.log("addHundred: " + JSON.stringify(addHundred));

    for (let i = 0; i < addHundred.length; i++) {
      //get rate for exact price
      let getExactRatePrice = findExactRatePrice(addHundred[i], ratesData);
      let exactRatePriceObj = getExactRatePrice[0];
      
      //if exact price is found push that rate and price to array object
      if (Object.keys(exactRatePriceObj).length > 0) {
        for (let key in exactRatePriceObj) {
          const ratePriceObj = {
            rate: key,
            price: exactRatePriceObj[key],
            desc: "NO POINTS!"
          }
          bpsPointsData.push(ratePriceObj);
        }
      } 
      else {
        //get odd rate and price
        findOddRatePrice(addHundred[i], ratesData, bpsPointsData);
      }
    }
    
    return bpsPointsData;
  }

  const findExactRatePrice = (priceToFind, ratesData) => {
    let exactRatePrice = [];
    exactRatePrice.push(Object.fromEntries(
      Object.entries(ratesData).filter(([key, value]) => value === priceToFind)
    ));
    
    return Object.values(exactRatePrice);
  }

  const findOddRatePrice = (priceToFind, ratesData, bpsPointsData) => {
    let lowerRatePrice = findLowerRatePrice(priceToFind, ratesData);
    let higherRatePrice = findHigherRatePrice(priceToFind, ratesData);
    let priceLowerToHigherRatePrice = findPriceLowerToHigherRatePrice(higherRatePrice[1], ratesData);
    let lowerRate, lowerPrice, higherRate, higherPrice;
    
    //If not an exact match, then we need to derive the rate that has the lookup price 
    //between the two rates.
    //Example: If the lookup value is 102.000, so we need to find a rate that has the first price of 
    //higher than 102. In the below example, the first price above 102 is 102.141 and correspondent rate 
    //is 6.125. Then we take the immediate lower rate than 6.125, i.e. 6.000 in this case with a price of 
    //101.766.
    //Now that we have a lower and a higher rate, 102.000 lookup price can be derived between 6.000 and 
    //6.125 by using the below formula.
    //targetRate = higherRate - ((higherPrice - targetPrice) * 0.6 / ((higherPrice - lowerPrice) / (higherRate - lowerRate))) --> new formaul as per new pricing engine please refer task SWP-82 task in Jira
    //example: 6.000 + (102.000 - 101.766) / ((102.141 - 101.766) / (6.125 - 6.000))
    //The answer would be 6.078. We need to display the rate as 6.078% at NO POINTS!
    if (priceLowerToHigherRatePrice.length > 0 && higherRatePrice.length > 0) {
      lowerRate = priceLowerToHigherRatePrice[0];
      lowerPrice = priceLowerToHigherRatePrice[1];
      higherRate = higherRatePrice[0];
      higherPrice = higherRatePrice[1];
     
      const diffInHigherLowerRate = higherRate - lowerRate;
      const diffInHigherLowerPrice = higherPrice - lowerPrice;
      const diffInHigherTargetPrice = higherPrice - priceToFind;
      const newOddRate = parseFloat(higherRate) - ((parseFloat(diffInHigherTargetPrice) * 0.5) / (parseFloat(diffInHigherLowerPrice) / parseFloat(diffInHigherLowerRate)));
        const ratePriceObj = {
        rate: newOddRate,
        price: "",
        desc: "NO POINTS!"
      }
      bpsPointsData.push(ratePriceObj);
    }

    //While deriving the rate, if there is no lower price than the lookup price, then take the first rate 
    //where the price is above the lookup price. Display that rate.
    //first higher price - lookup price. Let's assume
    //101.505 - 101.500 = 0.005 REBATE!, i.e. 7.000% at 0.005 REBATE!
    else if (lowerRatePrice.length === 0 && higherRatePrice.length > 0) {
      higherRate = higherRatePrice[0];
      higherPrice = higherRatePrice[1];

      const differenceInPrice = parseFloat(higherPrice - priceToFind).toFixed(3) + " REBATE!";
      const ratePriceObj = {
        rate: higherRate,
        price: higherPrice,
        desc: differenceInPrice
      }
      bpsPointsData.push(ratePriceObj);
    }

    //If a higher price is not available on any rate, to derive the price, then show the rate that has 
    //the closest to the lookup value available and display the discount points for that rate.
    //For example, lookup price is 102.000, but the maximum final price is 101.755, then take the rate at 
    //which we have the maximum final price (in this example 7.250%). the price to display will be
    //lookup the price - maximum final price available = x.xxx POINTS!
    //102.000 - 101.755 = 0.245 POINTS!
    //7.250% at 0.245 POINTS!
    else if (higherRatePrice.length === 0 && lowerRatePrice.length > 0) {
      lowerRate = lowerRatePrice[0];
      lowerPrice = lowerRatePrice[1];

      const differenceInPrice = parseFloat(priceToFind - lowerPrice).toFixed(3) + " POINTS!";
      const ratePriceObj = {
        rate: lowerRate,
        price: lowerPrice,
        desc: differenceInPrice
      }
      bpsPointsData.push(ratePriceObj);
    }
  }

  
  const findLowerRatePrice = (priceToFind, ratesData) => {
    let lowerRatePriceArr  = [];
    lowerRatePriceArr.push(Object.fromEntries(
      Object.entries(ratesData).filter(([key, value]) => value < priceToFind)
    ));

    let lowerRatePriceObj = lowerRatePriceArr.pop();

    if (Object.keys(lowerRatePriceObj).length === 0) {
      return lowerRatePriceArr;
    }

    let lowerRatePrice = Object.entries(lowerRatePriceObj).pop();

    return Object.values(lowerRatePrice);
  }

  const findHigherRatePrice = (priceToFind, ratesData) => {
    let higherRatePriceArr  = [];
    higherRatePriceArr.push(Object.fromEntries(
      Object.entries(ratesData).filter(([key, value]) => value > priceToFind)
    ));

    let higherRatePriceObj = higherRatePriceArr.pop();
    
    if (Object.keys(higherRatePriceObj).length === 0) {
      return higherRatePriceArr;
    }

    let higherRatePrice = Object.entries(higherRatePriceObj)[0];

    return Object.values(higherRatePrice);
  }

  const findPriceLowerToHigherRatePrice = (higherRatePrice, ratesData) => {
    let exactRatePrice = findExactRatePrice(higherRatePrice, ratesData);
    const exactRatePriceObj = exactRatePrice[0];
    
    let lowerRate;
    let lowerRatePrice;
    for (const exactRate in exactRatePriceObj) {
      Object.keys(ratesData).forEach(rates => {  
        if (rates < exactRate) {
          lowerRate = rates;
          lowerRatePrice = ratesData[rates];
        }
      })
      break;
    }

    let lowerRateObj = {};
    lowerRateObj[lowerRate] = lowerRatePrice;
    let lowerRatePriceArr = [];
    lowerRatePriceArr.push(lowerRateObj);
    let lowerRatePriceObj = lowerRatePriceArr.pop();
    let lowerRatePriceEntries = Object.entries(lowerRatePriceObj)[0];
    return Object.values(lowerRatePriceEntries);
  }

  return (
    <>
      <Container className="body-wrapper pb-3" id="rateTable">
         <Row>
        <Col lg="6">
        <div style={{ position: "relative" }}>
        <div className="partner_benefits_title_div">
        <h2 className="partner_benefits_title_title">
            <div className="partner_benefits_title_part1_pricing_table">
              Government Pricing
            </div>
          </h2>
          </div>
          <Table responsive bordered className="custom-borders">
            <div id="watermark" className="newline containerbackground text-center">
              <span style={{ whiteSpace: "nowrap" }}>
                NMLS LICENSEES ONLY
              </span>
            </div>

            <thead className="product-table">
              <tr className="product-title">
              {tableHeaderName.map((headerName, i) => (
                    <th
                      key={i}
                      style={{ width: "20%", textAlign: "center" }}
                      className="newLine"
                    >
                      {headerName}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody className="product-table">
            {tableFormat[0]?.my_Commission_is.map((data, i) => {
                  return (
                      <tr key={i}>
                      <td
                        className="rates-cell"
                        style={{ textAlign: "center" }}
                      >
                        {
                          <>{tableFormat[0]?.my_Commission_is[i]}</>
                        }
                      </td>
                      {fhaData[i] && 
                        <FormulateRate dataArray={fhaData} i={i} />
                      } 
                    </tr> 
                  );
                })}
            </tbody>
          </Table>
          
        </div>
        <p className="table-disclaimer pl-4 pr-4" style={{ textAlign: "center" }}>
        Purchase, $375,000 loan amount, Primary home, Single Family Residence, 30 year fixed,
        740 FICO, 96.5% Loan-to-Value (LTV), CA State
        </p>
        </Col>
        <Col lg="6" >
        <div style={{ position: "relative" }}>
        <div className="partner_benefits_title_div">
          <h2 className="partner_benefits_title_title">
            <div className="partner_benefits_title_part1_pricing_table">
              Conventional Pricing
            </div>
          </h2>
          </div>
          <Table responsive bordered className="custom-borders">
            <div id="watermark" className="newline containerbackground text-center">
              <span style={{ whiteSpace: "nowrap" }}>
                NMLS LICENSEES ONLY
              </span>
            </div>
            <thead className="product-table">
              <tr className="product-title">
              {tableHeaderName.map((headerName, i) => (
                    <th
                      key={i}
                      style={{ width: "20%", textAlign: "center" }}
                      className="newLine"
                    >
                      {headerName}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody className="product-table">
            {tableFormat[0]?.my_Commission_is.map((data, i) => {
                  return (
                    <tr key={i}>
                      <td
                        className="rates-cell"
                        style={{ textAlign: "center" }}
                      >
                        {
                          <>{tableFormat[0]?.my_Commission_is[i]}</>
                        }
                      </td>
                      {convData[i] && <FormulateRate dataArray={convData} i={i} />}
                    </tr>
                  );
                })}
            </tbody>
          </Table>
        </div>
        <p className="table-disclaimer pl-4 pr-4" style={{ textAlign: "center" }}>
        Purchase, $375,000 loan amount, Primary home, Single Family Residence, 30 year fixed,
        740 FICO, 60% Loan-to-Value (LTV), CA State
        </p>
        </Col>
        </Row> 
        <div className="partner_benefits_title_div2">
          <h2 className="day-date">
            As of{" "}
            {new Date().toLocaleDateString("en-us", {
              weekday: "long",
              month: "long",
              day: "numeric",
            })}
            &nbsp;
            {new Date().toLocaleTimeString("en-US", {
              timeStyle: "short",
              timeZone: "America/Los_Angeles",
            })}
          </h2>
        </div>

        <p className="table-disclaimer pb-0" style={{ textAlign: "center" }}>
          *Indicative Rates. Call us to learn more.
          <br />
          The borrower is charged customary lender fees.
        </p>
        
        <Button
                  variant="primary"
                  className="priceButton mt-3"    
                  onClick={() =>
                    window.open(process.env.REACT_APP_SWMC_URL, "_blank")
                  }                           
                >
                  Price Your Scenario
                </Button>
              
      </Container>
      <Modal
        id="downloadModal"
        show={downloadModal}
        onHide={closeModal}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body className="p-0">
          <Suspense
            fallback={
              <div className="text-center">
                <Spinner />
              </div>
            }
          >
            <ApplyAndDownloadForm sunwestLink={props.sunwestLink} sunwestFullLink={props.sunwestFullLink} celligenceLink={props.celligenceLink} celligenceLlcLink={props.celligenceLlcLink} />
          </Suspense>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default PricingTable;
